import { FC, useEffect, useState } from 'react';
import { Page } from '@/components/Page.tsx';
import { Section, Cell, Button, Caption, Spinner } from '@telegram-apps/telegram-ui';
import { TbChevronRight, TbPlus } from 'react-icons/tb';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import { useTranslation } from 'react-i18next';
import { getSecretSantaGames } from '@/api/apiMethods.ts';
import { SecretSantaGame } from '@/api/types.ts';
import { useAuthUser } from '@/providers';
import {haptic} from "@/helpers/haptic.ts";

export const SecretSantaListPage: FC = () => {
  const { navigateToRoute, showSnackbar } = useNavigation();
  const { t, i18n: { language } } = useTranslation();
  const { authUser } = useAuthUser();
  const [games, setGames] = useState<SecretSantaGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  if (!authUser) {
    throw new Error('SecretSantaListPage must be used within a AuthProvider');
  }

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setIsLoading(true);
        const fetchedGames = await getSecretSantaGames();
        setGames(fetchedGames);
      } catch (error) {
        console.error(error)
        haptic.notification('error');
        showSnackbar("❌", "secretSanta.error.loadFailed");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGames().then();
  }, []);

  const myGames = games.filter(game => game.organizer_id === authUser.id);
  const participatingGames = games.filter(game => 
    game.organizer_id !== authUser.id &&
    game.participants.some(p => p.user_id === authUser.id)
  );

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusText = (status: string) => {
    return t(`secretSanta.status.${status}`);
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex justify-center items-center p-10">
          <Spinner size="l" />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="flex flex-col gap-3 pb-10 ">
        {myGames.length > 0 && (
          <Section header={t('secretSanta.myGames')}>
            {myGames.map((game) => (
              <Cell
                key={game.id}
                description={
                  <div className="flex items-center gap-2">
                    {game.event_date && <Caption>{formatDate(game.event_date)}</Caption>}
                    <Caption>•</Caption>
                    <Caption>{getStatusText(game.status)}</Caption>
                  </div>
                }
                after={
                  <Caption weight="2" className="flex gap-1 items-center tg-link-color">
                    {t('secretSanta.participants.count', { count: game.participants.length })}
                    <TbChevronRight size={20} />
                  </Caption>
                }
                onClick={() => {
                  navigateToRoute(`/main/secret-santa/${game.id}`);
                }}
              >
                {game.title}
              </Cell>
            ))}
          </Section>
        )}

        {participatingGames.length > 0 && (
          <Section header={t('secretSanta.participant')}>
            {participatingGames.map((game) => (
              <Cell
                key={game.id}
                description={
                  <div className="flex items-center gap-2">
                    {game.event_date && <Caption>{formatDate(game.event_date)}</Caption>}
                    <Caption>•</Caption>
                    <Caption>{getStatusText(game.status)}</Caption>
                  </div>
                }
                after={
                  <Caption weight="2" className="flex gap-1 items-center tg-link-color">
                    {t('secretSanta.participants.count', { count: game.participants.length })}
                    <TbChevronRight size={20} />
                  </Caption>
                }
                onClick={() => {
                  navigateToRoute(`/main/secret-santa/${game.id}`);
                }}
              >
                {game.title}
              </Cell>
            ))}
          </Section>
        )}

        {games.length === 0 && (
          <div className="p-10 text-center">
            <Caption className="text-color-hint mb-4">
              {t('secretSanta.emptyState.noGames')}
            </Caption>
            <Caption className="text-color-hint">
              {t('secretSanta.emptyState.createFirst')}
            </Caption>
          </div>
        )}

        <div className="p-3">
          <Button
            stretched={true}
            onClick={() => {
              navigateToRoute('/main/secret-santa/new');
            }}
            before={<TbPlus size={22} />}
            mode="filled"
          >
            {t('secretSanta.create')}
          </Button>
        </div>
      </div>
    </Page>
  );
};
